import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Alert,
    DialogActions,
    Box,
    Typography,
    Button,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Grid,
    TextField
} from '@mui/material';
import {
    People as PeopleIcon,
    Delete as DeleteIcon,
    Face as FaceIcon
} from '@mui/icons-material';
import { addDoc, collection, doc, updateDoc, onSnapshot, query, where, deleteField } from 'firebase/firestore';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { db, auth } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';

interface FamilyMember {
    id: string;
    firstName: string;
    lastName: string;
    nickname: string;
}

interface UserProfile {
    firstName: string;
    lastName: string;
    nickname: string;
    city: string;
    team?: string;
}

interface FamilyManagementProps {
    userProfile: UserProfile;
}

export const FamilyManagement: React.FC<FamilyManagementProps> = ({ userProfile }) => {
    const { user } = useAuth();
    const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
    const [showAddFamily, setShowAddFamily] = useState(false);
    const [newFamilyMember, setNewFamilyMember] = useState({
        firstName: '',
        lastName: '',
        nickname: '',
    });

    // Etats pour la suppression sécurisée
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [deletePassword, setDeletePassword] = useState('');
    const [memberToDelete, setMemberToDelete] = useState<string | null>(null);

    // Charger les membres de la famille
    useEffect(() => {
        if (user) {
            const q = query(collection(db, 'users'), where('parentId', '==', user.uid));
            const unsubscribe = onSnapshot(q, (snapshot) => {
                const members = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                })) as FamilyMember[];
                // Filtrer les membres "supprimés" (anonymisés)
                const activeMembers = members.filter(m => m.firstName !== 'Parti' && m.lastName !== 'PARTI');
                setFamilyMembers(activeMembers);
            });
            return () => unsubscribe();
        } else {
            setFamilyMembers([]);
        }
    }, [user]);

    const handleAddFamilyMember = async () => {
        if (!user || !newFamilyMember.firstName || !newFamilyMember.lastName || !newFamilyMember.nickname) {
            alert("Veuillez remplir tous les champs obligatoires (Prénom, Nom, Surnom).");
            return;
        }

        try {
            await addDoc(collection(db, 'users'), {
                firstName: newFamilyMember.firstName,
                lastName: newFamilyMember.lastName,
                nickname: newFamilyMember.nickname,
                parentId: user.uid,
                isChild: true,
                verified: true, // Directement vérifié (Bronze)
                role: 'bronze', // Rôle par défaut
                city: userProfile.city, // Hérite de la ville du parent
                team: userProfile.team || 'Enfant de la Tribu', // Hérite de la team ou valeur par défaut
                createdAt: new Date().toISOString(),
                email: '', // Pas d'email pour les enfants
            });

            // Réinitialiser le formulaire
            setNewFamilyMember({
                firstName: '',
                lastName: '',
                nickname: '',
            });
            setShowAddFamily(false);
        } catch (error) {
            console.error("Erreur lors de l'ajout du membre:", error);
            alert("Une erreur est survenue lors de l'ajout.");
        }
    };

    const handleDeleteClick = (memberId: string) => {
        setMemberToDelete(memberId);
        setOpenDeleteDialog(true);
        setDeletePassword('');
    };

    const handleConfirmDelete = async () => {
        if (!auth.currentUser || !auth.currentUser.email || !memberToDelete) return;

        try {
            // 1. Ré-authentification
            const credential = EmailAuthProvider.credential(auth.currentUser.email, deletePassword);
            await reauthenticateWithCredential(auth.currentUser, credential);

            // 2. Anonymisation
            await updateDoc(doc(db, 'users', memberToDelete), {
                firstName: "Parti",
                lastName: "PARTI",
                // On garde le nickname
                // On garde la team
                city: "",
                parentId: deleteField() // Supprime le champ parentId
            });

            setOpenDeleteDialog(false);
            setMemberToDelete(null);
            setDeletePassword('');

        } catch (error: any) {
            console.error("Erreur lors de la suppression:", error);
            if (error.code === 'auth/wrong-password') {
                alert("Mot de passe incorrect.");
            } else if (error.code === 'auth/requires-recent-login') {
                alert("Veuillez vous reconnecter.");
            } else {
                alert("Erreur: " + error.message);
            }
        }
    };

    return (
        <Box sx={{
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper',
            mb: 2
        }}>
            <Typography variant="h6" component="div" align="center" gutterBottom>
                Ma Tribu
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" paragraph>
                (Comptes rattachés)
            </Typography>

            <Typography variant="body2" color="text.primary" align="center" paragraph>
                Pour inscrire vos proches (enfants, conjoint...) qui n'ont pas d'adresse email ou ne souhaitent pas créer de compte.
            </Typography>

            {!showAddFamily && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PeopleIcon />}
                        onClick={() => {
                            setNewFamilyMember({
                                ...newFamilyMember,
                                lastName: userProfile.lastName // Pré-remplir le nom de famille
                            });
                            setShowAddFamily(true);
                        }}
                    >
                        Ajouter un membre
                    </Button>
                </Box>
            )}

            {showAddFamily && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'rgba(0,0,0,0.02)' }}>
                    <Typography variant="subtitle2" gutterBottom>Nouveau membre</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <TextField
                                label="Prénom"
                                size="small"
                                fullWidth
                                value={newFamilyMember.firstName}
                                onChange={(e) => setNewFamilyMember({ ...newFamilyMember, firstName: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Nom"
                                size="small"
                                fullWidth
                                value={newFamilyMember.lastName}
                                onChange={(e) => setNewFamilyMember({ ...newFamilyMember, lastName: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Surnom"
                                size="small"
                                fullWidth
                                value={newFamilyMember.nickname}
                                onChange={(e) => setNewFamilyMember({ ...newFamilyMember, nickname: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button size="small" onClick={() => setShowAddFamily(false)}>Annuler</Button>
                            <Button size="small" variant="contained" onClick={handleAddFamilyMember}>Ajouter</Button>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {familyMembers.length === 0 ? (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ fontStyle: 'italic' }}>
                    Aucun membre dans votre tribu pour le moment.
                </Typography>
            ) : (
                <List dense sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
                    {familyMembers.map((member) => (
                        <ListItem
                            key={member.id}
                            secondaryAction={
                                <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteClick(member.id)}>
                                    <DeleteIcon />
                                </IconButton>
                            }
                            sx={{ borderBottom: '1px solid rgba(0,0,0,0.05)' }}
                        >
                            <ListItemIcon>
                                <FaceIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary={`${member.firstName} ${member.lastName} (${member.nickname})`}
                            />
                        </ListItem>
                    ))}
                </List>
            )}

            {/* Dialog Confirmation Suppression */}
            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ color: 'error.main' }}>Supprimer ce membre ?</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Attention, cette action est irréversible.
                    </Alert>
                    <Typography variant="body1">
                        Cette personne sera retirée de votre tribu et ses données personnelles seront effacées.
                    </Typography>
                    <TextField
                        label="Confirmez votre mot de passe"
                        type="password"
                        fullWidth
                        margin="normal"
                        value={deletePassword}
                        onChange={(e) => setDeletePassword(e.target.value)}
                        helperText="Requis pour confirmer la suppression"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)}>Annuler</Button>
                    <Button variant="contained" color="error" onClick={handleConfirmDelete}>
                        Oui, supprimer
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
